import React, { useRef } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { AtlassianIcon } from '@atlaskit/logo';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import wacToken from '@/libraries/design/foundations/token/src/wac-token';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { navGrid, navLayout } from './nav-layout';
import { GlobalNavProvider } from './nav-links-container';
import { NavUtilities } from './nav-utilities';
import { SkipToContent } from './skip-to-content';

export type SimplifiedProductNavProps = {
	readonly productLogo: JSX.Element;
	/** Product wordmark height in pixels, shared across all viewports. */
	readonly productLogoHeight?: number;
	readonly productLogoUrl: string;
	readonly productLogoAriaLabel: string;
	readonly productKey?: ProductKeyTypes;
	readonly isShowTryNowLink?: boolean;
	readonly isShowUserProfile?: boolean;
	readonly navigationTrynowCtaText?: string;
	readonly navigationTrynowCtaUrl?: string;
	readonly navigationTrynowCtaOnClick?: (e: React.MouseEvent) => void;
	readonly navigationTrynowCtaButtonVariant?: 'primary' | 'secondary';
};

const simplifiedProductPageNavStyle = css({
	position: 'sticky',
	insetBlockStart: 0,
	zIndex: 5,
	display: 'grid',
	gridTemplateColumns: 'minmax(0, 1fr)',
	alignItems: 'center',
	width: '100%',
	height: navLayout.height,
	[media.above.sm]: { gridTemplateColumns: navGrid.productColumnsDesktop },
	[media.above.lg]: { gridTemplateColumns: navGrid.productColumnsWide },
	backgroundColor: wacToken('color.neutral.light0'),

	// This is a workaround until this nav gets replaced for AI SDLC page (/software/jira/dev) to hide the username on small screens
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.avatar-button span[aria-hidden="true"]': {
		[media.below.sm]: {
			display: 'none',
		},
	},
});

const simplifiedProductPageNavStyleLoggedOut = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'button[name="sign-in"]': {
		[media.below.sm]: {
			display: 'none',
		},
	},
});

const simplifiedNavAtlasIconContainerStyle = css({
	display: 'none',

	[media.above.sm]: {
		height: '68px',
		width: '68px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		backgroundColor: wacToken('color.primary.blue'),
	},
});

const simplifiedNavAtlasIconStyle = css({
	height: '56px',
	width: '56px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const simplifiedNavContentWrapperStyle = css({
	minWidth: 0,
	height: '100%',
	paddingInline: navLayout.outerInset,
	[media.above.sm]: { gridColumn: 3, paddingInlineStart: 0 },
});

const simplifiedProductPageNavDarkThemeStyle = css({
	backgroundColor: wacToken('color.primary.black'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"button[name='sign-in'] span": {
		color: wacToken('color.neutral.light0'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.avatar-button span': {
		color: wacToken('color.neutral.light0'),
	},
});

const simplifiedProductPageNavContentStyle = css({
	boxSizing: 'border-box',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: token('space.300'),
	width: '100%',
	height: '100%',
	marginInline: 'auto',
	[media.below.sm]: {
		gap: token('space.150'),
	},
});

const simplifiedProductPageNavLogoLinkStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	position: 'relative',
	insetBlockStart: '1.5px',
	textDecoration: 'none',
	height: 'var(--product-nav-logo-height, 24px)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: { display: 'flex', height: 'var(--product-nav-logo-height, 24px)', width: 'auto' },
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	svg: { height: 'var(--product-nav-logo-height, 24px)', width: 'auto', maxWidth: 'none' },
});

const simplifiedProductPageNavActionsStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	flexShrink: 0,
	visibility: 'hidden',
});

export const SimplifiedProductNav = ({
	productLogo,
	productLogoHeight = 24,
	productLogoUrl,
	productLogoAriaLabel,
	productKey,
	isShowTryNowLink = true,
	isShowUserProfile = true,
	navigationTrynowCtaText,
	navigationTrynowCtaUrl,
	navigationTrynowCtaOnClick,
	navigationTrynowCtaButtonVariant = 'primary',
}: SimplifiedProductNavProps) => {
	const intl = useIntl();
	const { isDarkTheme } = useTheme();
	const [, setIsOpenSearch] = React.useState(false);
	const searchAndProfileRef = useRef<HTMLDivElement | null>(null);

	const defaultCtaText = intl.formatMessage({
		id: 'productpagesubnav.try-now-button-label',
		defaultMessage: 'Get it free',
	});

	const { isUserLoggedIn, requestInProgress } = useLoggedInUser();

	const defaultCtaUrl = useLocalizedHref(
		'/try/cloud/signup?application=wac&signupSource=jira-ai-native',
	);

	const themedProductLogo =
		React.isValidElement<{ darkTheme?: boolean }>(productLogo) &&
		typeof productLogo.type !== 'string'
			? React.cloneElement(productLogo, { darkTheme: isDarkTheme })
			: productLogo;

	return (
		<GlobalNavProvider globalNavItemKeys={[]}>
			<header
				style={{ '--product-nav-logo-height': `${productLogoHeight}px` } as React.CSSProperties}
				className="simplified-product-nav-container"
				css={[
					simplifiedProductPageNavStyle,
					!isUserLoggedIn && simplifiedProductPageNavStyleLoggedOut,
					isDarkTheme && simplifiedProductPageNavDarkThemeStyle,
				]}
				data-testid="simplified-product-nav"
			>
				<a
					css={simplifiedNavAtlasIconContainerStyle}
					href={useLocalizedHref('/')}
					aria-label={intl.formatMessage({
						id: 'simplifiedProductSubNav.atlassian-logo-label',
						defaultMessage: 'Atlassian home',
					})}
					data-event="clicked"
					data-event-component="linkButton"
					data-event-container="productPageNav"
					data-product-key={productKey}
				>
					<span css={simplifiedNavAtlasIconStyle}>
						<AtlassianIcon size="medium" appearance="inverse" />
					</span>
				</a>
				<div css={simplifiedNavContentWrapperStyle}>
					<nav
						aria-label={intl.formatMessage({
							id: 'productPageNav.main-nav-label',
							defaultMessage: 'Main',
						})}
						css={simplifiedProductPageNavContentStyle}
					>
						<SkipToContent />
						<a
							className="navigation-bar-logo-link"
							css={simplifiedProductPageNavLogoLinkStyle}
							href={productLogoUrl}
							aria-label={productLogoAriaLabel}
							data-event="clicked"
							data-event-component="linkButton"
							data-event-container="productPageNav"
							data-product-key={productKey}
						>
							{themedProductLogo}
						</a>
						<div
							css={[
								simplifiedProductPageNavActionsStyle,
								!requestInProgress && css({ visibility: 'visible' }),
							]}
						>
							<NavUtilities
								isShowTryNowLink={isShowTryNowLink}
								productKey={productKey}
								isShowSearchButton={false}
								setIsOpenSearch={setIsOpenSearch}
								navigationTrynowCtaText={navigationTrynowCtaText ?? defaultCtaText}
								navigationTrynowCtaUrl={navigationTrynowCtaUrl ?? defaultCtaUrl}
								navigationTrynowCtaOnClick={navigationTrynowCtaOnClick}
								navigationTrynowCtaButtonVariant={navigationTrynowCtaButtonVariant}
								isDisplayDivider={false}
								searchAndProfileRef={searchAndProfileRef}
								isShowAppSwitcher={false}
								isShowUserProfile={isShowUserProfile}
								navType="productNav"
							/>
						</div>
					</nav>
				</div>
			</header>
		</GlobalNavProvider>
	);
};
